import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Rocket, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Onboarding-Fortschritts-Badge — schließt die Lücke zwischen
 * "Stripe-Checkout erfolgreich" und "Mandant einsatzbereit".
 *
 * Erscheint NUR für agency_admin/platform_admin, NUR wenn es mind. 1 Mandanten
 * in status='setup' + onboarding_completed=false gibt.
 *
 * Klick öffnet Popover mit Liste. Klick auf Mandant: switch + nav zu /onboarding/client.
 */
export default function OnboardingProgressBadge() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState("");

  const isAgencyAdmin = user?.role === "agency_admin" || user?.role === "platform_admin";

  useEffect(() => {
    if (!isAgencyAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/agency/clients");
        if (cancelled) return;
        const setup = (r.data?.clients || []).filter(
          (c) => c.status === "setup" && c.onboarding_completed === false
        );
        setPending(setup);
      } catch {
        /* still */
      }
    };
    load();
    // Sanfter Refresh alle 45s, falls Webhook während User-Session ankommt
    const t = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAgencyAdmin]);

  const onJump = async (company_id) => {
    setSwitching(company_id);
    try {
      await api.post("/agency/switch-client", { company_id });
      await checkAuth();
      setOpen(false);
      nav("/onboarding/client");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wechsel fehlgeschlagen");
    } finally {
      setSwitching("");
    }
  };

  if (!isAgencyAdmin || pending.length === 0) return null;

  const label =
    pending.length === 1
      ? "1 Mandant wartet auf Setup"
      : `${pending.length} Mandanten warten auf Setup`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="onboarding-progress-badge"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 transition-colors"
        >
          <Rocket className="w-3 h-3" />
          <span>{label}</span>
          <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-600 text-white text-[10px] leading-none">
            {pending.length}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-2"
        data-testid="onboarding-progress-popover"
      >
        <div className="px-2 py-1.5">
          <p className="text-xs font-bold text-slate-900">Mandanten im Setup</p>
          <p className="text-[11px] text-slate-500">Schließe das Branding-Onboarding ab, damit Angebote im Mandanten-Look versendet werden.</p>
        </div>
        <div className="space-y-1 mt-1 max-h-72 overflow-y-auto">
          {pending.map((c) => (
            <button
              key={c.company_id}
              type="button"
              data-testid={`onboarding-jump-${c.company_id}`}
              disabled={switching === c.company_id}
              onClick={() => onJump(c.company_id)}
              className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-orange-50 group text-left disabled:opacity-50"
            >
              <div className="w-8 h-8 rounded-md bg-orange-100 grid place-items-center shrink-0">
                <Rocket className="w-4 h-4 text-orange-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{c.company_name}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {c.plan_name?.replace(/^Client /, "")} · {c.email}
                </p>
              </div>
              {switching === c.company_id ? (
                <Loader2 className="w-4 h-4 text-orange-600 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-orange-600 shrink-0" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
